import { CheckOutlined } from '@ant-design/icons';
import { useMutation } from '@tanstack/react-query';
import { Modal, Space, Typography } from 'antd';
import React, { useState } from 'react';
import type { AllocationResult, BulkAllocationInput } from '../../DTOS/ResourcePlanning';
import api from '../../services/resourcePlanning/ResourcePlanningService';
import { Warnings } from './AllocationModal';
import { useFeedback } from './shared';

interface Pending { input: BulkAllocationInput; label: string; result: AllocationResult; details?: React.ReactNode }

/**
 * Every bulk change is checked on the server first (preview), shown with its resulting overloads,
 * and only written after confirmation. The whole change is one transaction.
 */
export const useBulkChange = (onSaved?: () => void) => {
  const feedback = useFeedback();
  const [pending, setPending] = useState<Pending | null>(null);
  const run = useMutation({ mutationFn: (fn: () => Promise<unknown>) => fn(), onError: feedback.error });
  const check = (input: BulkAllocationInput, label: string, details?: React.ReactNode) =>
    run.mutate(async () => setPending({ input, label, details, result: await api.bulk({ ...input, previewOnly: true }) }));
  const apply = () => run.mutate(async () => {
    const result = await api.bulk({ ...pending!.input, previewOnly: false });
    feedback.success(result.warnings.length ? `Modificari salvate. ${result.warnings.length} depasiri zilnice raman marcate.` : 'Modificari salvate.');
    setPending(null); onSaved?.();
  });
  const confirm = <Modal open={!!pending} title={pending?.label} onCancel={() => setPending(null)} onOk={apply} confirmLoading={run.isPending}
    okText={<span><CheckOutlined /> Salveaza</span>} cancelText='Renunta' width={560} destroyOnHidden>
    {pending && <Space direction='vertical' style={{ width: '100%' }}>
      <Typography.Text>{pending.result.planned} alocari zilnice vor fi modificate intr-o singura operatie.</Typography.Text>
      {pending.details}
      <Warnings result={pending.result} />
    </Space>}
  </Modal>;
  return { check, busy: run.isPending, confirm };
};
